import React, { type MouseEvent, ReactNode, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import {
    ConfirmationModal,
    ConfirmationModalProps,
    type ItemActionsProps,
    useL2DTheme,
    useModal,
} from '@europrocurement/flexy-components';
import { useFileUploadProps } from '@europrocurement/l2d-modules/modules/ParcoursFormalite/hooks/useFileUploadProps';
import DocumentItem from '../DocumentItem/DocumentItem';
import UploadableDocument from '../UploadableDocument/UploadableDocument';
import { Document } from '../../../providers/types';
import { DossierFichier } from '../../../hooks';

export type RequiredDocumentItemProps = {
    onChange: (files: FileList) => void;
    onDeletion: () => void;
    onDocDeletion: () => void;
    onDownload: (e?: MouseEvent) => void;
    onShow: (e?: MouseEvent) => void;
    onSign?: (e?: MouseEvent) => void;
    onDownloadSigned?: (e?: MouseEvent) => void;
    onMove: (e?: MouseEvent) => void;
    onClick: (e?: MouseEvent) => void;
    uploadable: boolean;
    uploading: boolean;
    editable: boolean;
    showable: boolean;
    signable: boolean;
    canDownloadSigned: boolean;
    movable: boolean;
    downloadable: boolean;
    removable: boolean;
    removableDoc: boolean;
    customAction?: ReactNode;
    file?: DossierFichier;
    onClickOnCard?: (e: MouseEvent) => void;
    isLocked?: boolean;
    isActivelySubmitting?: boolean;
} & Document;

const RequiredDocumentItem: React.FunctionComponent<RequiredDocumentItemProps> = function (props) {
    const {
        onChange: handleChange,
        onDeletion: handleDeletion,
        onDocDeletion: handleDocumentDeletion,
        onDownload: handleDownload,
        onClickOnCard: handleCardClick,
        onShow: handleShow,
        onSign: handleSign,
        onDownloadSigned: handleDownloadSigned,
        onClick: handleClick,
        onMove: handleMove,
        uploadable,
        uploading,
        editable,
        showable,
        signable,
        canDownloadSigned,
        downloadable,
        removable,
        removableDoc,
        movable,
        customAction,
        label,
        fileName,
        type,
        status,
        feedback,
        file,
        isLocked = false,
        isActivelySubmitting = false,
    } = props;
    const [isMouseHover, setIsMouseHover] = useState<number>(0);
    const [isDragHover, setIsDragHover] = useState<number>(0);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { accept, maxMbSize } = useFileUploadProps();
    const { palette } = useL2DTheme();

    const handleMouseEnter = () => setIsMouseHover((val) => val + 1);
    const handleMouseLeave = () => setIsMouseHover((val) => val - 1);
    const handleDragEnter = () => setIsDragHover((val) => val + 1);
    const handleDragLeave = () => setIsDragHover((val) => val - 1);
    const handleValidationError = (err: Error) => {
        enqueueSnackbar(<Typography>{err.message}</Typography>, { variant: 'error' });
    };

    const { modalActions } = useModal();
    const fileDeleteModalProps: ConfirmationModalProps = {
        color: palette.error,
        messages: {
            question: 'Supprimer ce fichier ?',
            detail: {
                message: 'Êtes vous sur de vouloir supprimer ce fichier?',
                severity: 'info',
            },
        },
        actionOnValidation: () => {
            modalActions.close();
            handleDeletion();
        },
        actionOnCancellation: () => {
            modalActions.close();
        },
    };

    const itemActions: ItemActionsProps = {
        disabled: isLocked || isActivelySubmitting,
        ...(showable && {
            onShow: (e) => handleShow(e ?? undefined),
            showTooltip: 'Afficher le document',
        }),
        ...(signable &&
            handleSign && {
                onSign: (e) => {
                    if (!isLocked) handleSign(e ?? undefined);
                },
                signTooltip: 'Signer le document',
            }),
        ...(canDownloadSigned &&
            handleDownloadSigned && {
                onDownloadSigned: (e) => {
                    if (e) e.stopPropagation();
                    if (!isLocked) handleDownloadSigned();
                },
                downloadSignedTooltip: 'Télécharger le document signé',
            }),
        ...(downloadable && {
            onDownload: (e) => {
                if (!isLocked) handleDownload(e ?? undefined);
            },
            downloadTooltip: 'Télécharger le document',
        }),
        ...(uploadable && {
            onUpload: () => {
                if (!isLocked) fileInputRef.current?.click();
            },
            uploadTooltip: uploadable && !editable ? 'Ajouter un fichier' : 'Modifier le document',
        }),
        ...(movable && {
            onMove: (e) => {
                if (!isLocked) handleMove(e ?? undefined);
            },
            moveTooltip: 'Déplacer le document',
        }),
        ...(removable && {
            onDelete: (e: React.MouseEvent) => {
                if (e) e.stopPropagation();
                if (!isLocked) modalActions.call(<ConfirmationModal {...fileDeleteModalProps} />);
            },
            deleteTooltip: 'Supprimer le fichier',
            confirmation: true,
        }),
        ...(!removable &&
            removableDoc && {
                onDelete: (e: React.MouseEvent) => {
                    if (e) e.stopPropagation();
                    if (!isLocked)
                        modalActions.call(
                            <ConfirmationModal
                                {...fileDeleteModalProps}
                                actionOnValidation={() => {
                                    modalActions.close();
                                    handleDocumentDeletion();
                                }}
                                messages={{
                                    question: 'Supprimer ce document ?',
                                    detail: {
                                        message: 'Êtes vous sur de vouloir supprimer ce document ?',
                                        severity: 'info',
                                    },
                                }}
                            />,
                        );
                },
                deleteTooltip: 'Supprimer le document',
                confirmation: true,
            }),
    };

    const item = (
        <DocumentItem
            label={label as string}
            fileName={fileName}
            type={type}
            status={status}
            feedback={feedback}
            hover={isMouseHover > 0}
            highlight={isDragHover > 0}
            uploadable={uploadable}
            uploading={isActivelySubmitting || uploading}
            itemActions={itemActions}
            customAction={customAction}
            sx={{ cursor: 'pointer' }}
            file={file}
            clickOnCard={handleCardClick}
        />
    );

    if (!uploadable) {
        return (
            <Box
                onClick={(e) => handleClick(e)}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {item}
            </Box>
        );
    }

    return (
        <UploadableDocument
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onValidationError={handleValidationError}
            onChange={handleChange}
            accept={accept}
            maxMbSize={maxMbSize}
            fileInputRef={fileInputRef}
            isSubmitting={isLocked}
        >
            {item}
        </UploadableDocument>
    );
};

export default RequiredDocumentItem;
